import HealthMonitor from '@omni/kit/HealthMonitor';
import {
  KitButton,
  KitIcon,
  KitInput,
  KitListItem,
  KitText,
  KitToggle,
  KitTouchable,
} from '@omni/kit/components';
import KitDatePickerInput from '@omni/kit/components/KitDatePickerInput/KitDatePickerInput';
import KitEditableImage from '@omni/kit/components/KitEditableImage/KitEditableImage';
import KitSnack from '@omni/kit/components/KitSnack';
import { KitSnackDuration } from '@omni/kit/components/KitSnack.types';
import KitTimePicker from '@omni/kit/components/KitTimePicker';
import { RefType } from '@omni/kit/components/KitTimePicker/types';
import { useOrientationContext } from '@omni/kit/contexts/OrientationContext';
import { useShellContext } from '@omni/kit/contexts/ShellContext';
import { SizeClass, useSizeClass } from '@omni/kit/contexts/SizeClassContext';
import { useFeatureFlag } from '@omni/kit/hooks/useFeatureFlag';
import { useTranslation } from '@omni/kit/hooks/useTranslation';
import GroupsService from '@omni/kit/services/GroupsService';
import {
  IGroupEvent,
  IGroupLocation,
} from '@omni/kit/services/GroupsService/Types';
import ImageService from '@omni/kit/services/ImageService';
import { UNLEASH_TOGGLES } from '@omni/kit/services/UnleashService';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import { getDeviceType } from '@omni/kit/utilities/NativeHelpers';
import { excludedStatusCodes } from '@omni/kit/utilities/excludedStatusCodes';
import {
  getTimestampUTC,
  roundToNextHour,
} from '@omni/kit/utilities/utilities';
import {
  NavigationProp,
  RouteProp,
} from '@react-navigation/core/lib/typescript/src/types';
import {
  ParamListBase,
  useNavigation,
  useRoute,
} from '@react-navigation/native';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import moment from 'moment';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Platform,
  ScrollViewProps,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import Orientation from 'react-native-orientation-locker';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';

import { SendbirdUserMessage } from '../../../Types';
import { useChannelGroup } from '../../../hooks/useChannelGroup';
import useGetMessageIdForEventId from '../../../hooks/useGetMessageIdForEventId';
import PlatformKeyboardSpacer from '../../../mobile/components/PlatformKeyboardSpacer';
import {
  adjustEndDate,
  adjustEndTime,
} from '../../../utilities/dateTimeAdjustment';
import {
  passesDateRangeValidation,
  passesTimeRangeValidation,
} from '../../../utilities/dateTimeValidation';
import { parseRRule } from '../../../utilities/parseRrule';
import { generateFrequencyMessage } from '../../../utilities/repeatEventMessage';
import { formatDateForRules } from '../../../utilities/repeatEventRules';
import {
  calculateEndDateTime,
  getEventDuration,
} from '../../../utilities/repeatingEventDuration';
import Routes from '../../../web/navigation/Routes';
import * as Constants from '../../Constants';
import {
  getPrevMessageList,
  sendEventMessage,
  updateEventMessage,
} from '../../redux/actions/ChatActions';
import { accessTokenSelector, channelSelector } from '../../redux/selectors';
import GroupEventLocation from '../events/GroupEventLocationScreen';
import RepeatGroupEvent from '../events/RepeatGroupEventScreen';

const debug = require('debug')('tca:component:CreateEditGroupEventScreen');

type EventServiceFunction = (params: {
  body?: any;
  token?: string;
  id: string;
}) => Promise<any>;

type RepeatEventData = {
  frequency: string;
  stopEventDate: Date | null;
  startDate: Date;
  weekNumberInMonth: number;
  rrule: string;
};
export interface RouteProps extends RouteProp<ParamListBase, string> {
  params: {
    title?: string;
    event: IGroupEvent;
    channel: GroupChannel;
    groupEventLocation: IGroupLocation;
    activeMessage: SendbirdUserMessage;
    eventData?: RepeatEventData | null;
    setNewGroupEvent?: (event: IGroupEvent | undefined) => void;
  };
}

export default function CreateEditGroupEventScreen({
  groupEventInWebPlatform,
  onCancel,
  onSuccess,
}: {
  groupEventInWebPlatform?: IGroupEvent;
  onCancel?: (open?: boolean) => void;
  onSuccess?: (b?: boolean) => void;
}): ReactElement {
  const { sizeClass } = useSizeClass();
  const { t } = useTranslation();
  const { app, user } = useShellContext();

  const isEventDetailsScreen =
    Platform.OS === 'web'
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useRouteMatch(Routes.takeEventAttendanceScreen())?.isExact
      : false;

  const dispatch = useDispatch();
  const { isEnabled: isGroupAttendanceEnabled } = useFeatureFlag(
    UNLEASH_TOGGLES.DISPATCH_GROUP_ATTENDANCE
  );
  const isSmall = sizeClass === SizeClass.Small;
  const accessToken = useSelector(accessTokenSelector);
  // Navigation
  let navigation: NavigationProp<ParamListBase>;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    navigation = useNavigation();
  } catch {
    navigation = {} as NavigationProp<ParamListBase>; // Provide a fallback object
  }
  // Routes
  let route: RouteProps;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    route = useRoute<RouteProps>();
  } catch {
    route = {} as RouteProps;
  }
  const { setNewGroupEvent } = route?.params || {};
  const [deviceType, setDeviceType] = useState<string | null>(null);

  const event =
    Platform.OS === 'web' && !isSmall
      ? groupEventInWebPlatform
      : route?.params?.event;

  const timezone = event?.timezone || app.timezone?.name;

  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(true);

  const channel = useSelector(channelSelector);
  const [groupEventLocation, setEventGroupLocation] = useState<
    IGroupLocation | undefined
  >();
  // using it for published_at
  const currentDatetime = useMemo(() => new Date(), []);

  const [repeatingEventScreenVisible, setRepeatingEventScreenVisible] =
    useState(false);

  const [groupEventLocationScreenVisible, setGroupEventLocationScreenVisible] =
    useState(false);

  const { setLockedOrientation } = useOrientationContext();

  // Refs
  const titleRef = useRef<KitInput>(null);
  const startTimePickerRef = useRef<RefType>(null);
  const endTimePickerRef = useRef<RefType>(null);
  const startDateRef = useRef<RefType>(null);
  const endDateRef = useRef<RefType>(null);

  const [previousImageIds, setPreviousImageIds] = useState<string[]>([]);

  // Support both 'banner' and 'group events banner' types for backward compatibility
  // 'banner' was used before the backend change that standardized on 'group events banner'
  const [eventPhotoUrlValue, setEventPhotoUrlValue] = useState<
    string | undefined
  >(
    event
      ? event?._embedded?.images?.filter(
          (image) =>
            image.type === 'banner' || image.type === 'group events banner'
        )[0]?._links?.related?.href
      : undefined
  );
  const eventImageUrl = eventPhotoUrlValue && new URL(eventPhotoUrlValue);
  // use search-param "id", or fall-back to the /_source/<id>/image.png
  const eventImageId =
    eventImageUrl &&
    (eventImageUrl.searchParams.get('id') ||
      eventImageUrl.pathname.split('/').at(-2));

  const [imageTypesData, setImageTypesData] = useState<any[]>([]);

  // Initial states
  const channelData: GroupChannel = route?.params?.channel || channel;
  const initialTitle = event?.title || '';

  const groupEventChatList = useGetMessageIdForEventId(
    event?._embedded?.['repeating-event']?.id
      ? event?._embedded?.['repeating-event'].id
      : event?.id ?? ''
  );

  useEffect(() => {
    if (eventImageId) {
      // at to ids (and remove duplicates)
      setPreviousImageIds((ids) => [...new Set([...ids, eventImageId])]);
    }
  }, [eventImageId, setPreviousImageIds]);

  const deleteOldPhotos = useCallback(async (): Promise<number | undefined> => {
    for (const previousImageId of previousImageIds) {
      if (previousImageId === eventImageId) {
        continue;
      }

      try {
        const response = await ImageService.delete(
          app.appKey,
          'image',
          previousImageId,
          accessToken
        );

        if (response.status && !excludedStatusCodes.includes(response.status)) {
          HealthMonitor.logError(
            `ImageService.delete gave response ${response.status} for image ${previousImageId}`
          );
        }

        return response.status;
      } catch (error) {
        HealthMonitor.logError(error);
        debug(error);
      }
    }
  }, [accessToken, app.appKey, eventImageId, previousImageIds]);

  useEffect(() => {
    if (Platform.OS === 'web') return;

    let isMounted = true;

    const fetchDeviceTypeAndLock = async () => {
      try {
        const type = await getDeviceType();

        if (!isMounted) return;

        setDeviceType(type);

        if (type === 'handset') {
          Orientation.lockToPortrait();
        } else {
          Orientation.getOrientation((orientation) => {
            if (!isMounted) return;

            if (
              orientation === 'PORTRAIT' ||
              orientation === 'PORTRAIT-UPSIDEDOWN'
            ) {
              Orientation.lockToPortrait();
              setLockedOrientation('PORTRAIT');
            } else {
              Orientation.lockToLandscape();
              setLockedOrientation('LANDSCAPE');
            }
          });
        }
      } catch (e) {}
    };

    fetchDeviceTypeAndLock();

    return () => {
      isMounted = false;
      if (deviceType !== 'handset') {
        Orientation.unlockAllOrientations();
        setLockedOrientation(null);
      }
    };
  }, [setLockedOrientation, deviceType]);

  // Format utility to get dates from event if they have
  const initialStartDate: Date = useMemo(() => {
    if (event?.start_at) {
      // If event has start_at, use it as the initial start date
      return new Date(event?.start_at);
    }

    return new Date();
  }, [event?.start_at]);

  const initialEndDate: Date = useMemo(() => {
    if (event?.end_at) {
      return new Date(event?.end_at);
    }

    if (event?.start_at && event?.event_duration && timezone) {
      const startDate = new Date(event.start_at);
      const startTime = moment(event.start_at).tz(timezone).format('HHmm');

      const { endAt } = calculateEndDateTime(
        startDate,
        startTime,
        timezone,
        event.event_duration
      );

      return new Date(endAt);
    }

    return new Date();
  }, [event?.start_at, event?.event_duration, event?.end_at, timezone]);

  const initialStartTime: string | undefined = useMemo(() => {
    if (event) {
      if (timezone) {
        // Parse the ISO 8601 string and convert to the correct timezone
        return moment(event?.start_at).tz(timezone).format('HHmm');
      } // HHmm format (e.g., 1500 for 3:00 PM)
    }

    // Fallback to current time if no event or no start_at
    return String(
      timezone && roundToNextHour(moment().tz(timezone)).format('HHmm')
    ).padStart(4, '0');
  }, [event, timezone]);

  const initialEndTime: string | undefined = useMemo(() => {
    if (event?.end_at && timezone) {
      return moment(event?.end_at).tz(timezone).format('HHmm');
    }

    if (event?.start_at && event?.event_duration && timezone) {
      const startDate = new Date(event.start_at);
      const startTime = moment(event.start_at).tz(timezone).format('HHmm');

      const { endTime } = calculateEndDateTime(
        startDate,
        startTime,
        timezone,
        event.event_duration
      );

      return endTime;
    }

    return String(
      timezone &&
        roundToNextHour(moment().add(1, 'hour').tz(timezone)).format('HHmm')
    ).padStart(4, '0');
  }, [event?.start_at, event?.event_duration, event?.end_at, timezone]);

  const initialDescription = event?.description || '';
  const initialAllDay = event?.all_day || false;
  const initialAttendanceReminder = event?.send_attendance_reminder ?? true;
  // Computed States
  const [title, setTitle] = useState<string>(initialTitle);
  const [startDate, setStartDate] = useState<Date | undefined>(
    initialStartDate
  );

  const [endDate, setEndDate] = useState<Date | undefined>(initialEndDate);

  const [startTime, setStartTime] = useState<string | undefined>(() => {
    return initialStartTime && moment(initialStartTime, 'HHmm', true).isValid()
      ? initialStartTime
      : timezone &&
          String(
            roundToNextHour(moment().tz(timezone)).format('HHmm')
          ).padStart(4, '0');
  });

  const [eventDuration, setEventDuration] = useState<number>(60);
  const [eventDurationInDays, setEventDurationInDays] = useState<number>(0);

  const [passDateRangeValidation, setPassDateRangeValidation] =
    useState<boolean>(true);
  const [passTimeRangeValidation, setPassTimeRangeValidation] =
    useState<boolean>(true);

  const [endTime, setEndTime] = useState<string | undefined>(() => {
    // If initialEndTime is provided and valid, use it, otherwise return undefined
    return initialEndTime && moment(initialEndTime, 'HHmm', true).isValid()
      ? initialEndTime
      : timezone &&
          String(
            roundToNextHour(moment().add(1, 'hour').tz(timezone)).format('HHmm')
          ).padStart(4, '0');
  });

  const [allDay, setAllDay] = useState<boolean>(initialAllDay);
  const [attendanceReminder, setAttendanceReminder] = useState<boolean>(
    initialAttendanceReminder
  );
  const [description, setDescription] = useState<string>(initialDescription);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // Repeat data for web
  const [savedWebRepeatingData, setSavedWebRepeatingData] =
    useState<RepeatEventData | null>(null);

  const repeatingData = useMemo(() => {
    // Use `savedWebRepeatingData` for web and `route.params?.eventData` for mobile
    return savedWebRepeatingData ?? route.params?.eventData;
  }, [savedWebRepeatingData, route.params?.eventData]);

  const parsedRepeatingEventRule = useMemo(() => {
    const parsedRule = parseRRule(event?.repetition_rules?.[1]);

    if (parsedRule) {
      parsedRule.stopEventDate = parsedRule.stopEventDate ?? null;

      return parsedRule;
    }

    return null;
  }, [event?.repetition_rules]);

  const repeatSubtitle = useMemo(() => {
    if (repeatingData) {
      return generateFrequencyMessage(
        repeatingData?.frequency,
        repeatingData?.startDate,
        repeatingData?.stopEventDate,
        repeatingData?.weekNumberInMonth,
        t
      );
    }

    if (parsedRepeatingEventRule && event?.repetition_rules) {
      return generateFrequencyMessage(
        parsedRepeatingEventRule?.frequency,
        moment(event?.start_at).startOf('day').toDate(),
        parsedRepeatingEventRule?.stopEventDate ?? null,
        parsedRepeatingEventRule?.weekNumberInMonth ?? null,
        t
      );
    }

    return undefined; // If no valid data to generate the subtitle
  }, [
    repeatingData,
    parsedRepeatingEventRule,
    event?.start_at,
    t,
    event?.repetition_rules,
  ]);
  const group = useChannelGroup(channel?.url);

  const [eventLocation, setEventLocation] = useState<
    IGroupLocation | undefined
  >();
  const [isLocationEdited, setIsLocationEdited] = useState<boolean>(false);
  const [isLocationRemoved, setIsLocationRemoved] = useState<boolean>(false);
  const [isRepeatsEdited, setIsRepeatsEdited] = useState<boolean>(false);
  const [isEventImageEdited, setIsEventImageEdited] = useState<boolean>(false);

  const groupEventLocationData = route.params?.groupEventLocation
    ? route.params?.groupEventLocation
    : groupEventLocation
    ? groupEventLocation
    : eventLocation;

  const isEdited = useMemo(() => {
    if (
      !group ||
      !passDateRangeValidation ||
      !passTimeRangeValidation ||
      title === ''
    ) {
      return false;
    }

    return (
      title !== initialTitle ||
      description !== initialDescription ||
      startDate !== initialStartDate ||
      endDate !== initialEndDate ||
      startTime !== initialStartTime ||
      endTime !== initialEndTime ||
      isLocationEdited ||
      isRepeatsEdited ||
      isEventImageEdited ||
      allDay !== initialAllDay ||
      attendanceReminder !== initialAttendanceReminder
    );
  }, [
    title,
    initialTitle,
    startDate,
    initialStartDate,
    endDate,
    initialEndDate,
    startTime,
    initialStartTime,
    endTime,
    initialEndTime,
    passDateRangeValidation,
    passTimeRangeValidation,
    description,
    initialDescription,
    isLocationEdited,
    isRepeatsEdited,
    isEventImageEdited,
    group,
    allDay,
    initialAllDay,
    attendanceReminder,
    initialAttendanceReminder,
  ]);

  const createGroupEventService =
    !event &&
    repeatingData?.frequency !== 'never' &&
    repeatingData?.frequency !== undefined
      ? GroupsService.CreateRepeatingGroupEvent
      : GroupsService.CreateGroupEvent;

  const getUpdateGroupEventService = useCallback(() => {
    const isRepeatingEvent = event?.repetition_rules !== undefined;
    const isFrequencyNever =
      repeatingData?.frequency === 'never' ||
      repeatingData?.frequency === undefined;

    // Event is repeating, and repeatingData.frequency is not 'never'
    if (isRepeatingEvent && repeatingData?.frequency !== 'never') {
      return { updateEventService: GroupsService.UpdateRepeatingGroupEvent };
    }

    // Event is repeating, but repeatingData.frequency is 'never' or undefined
    if (isRepeatingEvent && isFrequencyNever) {
      return {
        deleteEventService: GroupsService.DeleteRepeatingGroupEvent,
        createEventService: GroupsService.CreateGroupEvent,
      };
    }

    // //  Event is not repeating, and repeatingData.frequency is not 'never'
    if (
      !isRepeatingEvent &&
      repeatingData?.frequency !== 'never' &&
      !isFrequencyNever
    ) {
      // Create a repeating event and delete the non-repeating event
      return {
        createEventService: GroupsService.CreateRepeatingGroupEvent,
        deleteEventService: GroupsService.DeleteGroupEvent,
      };
    }

    // Event is not repeating, and repeatingData.frequency is 'never' or undefined
    if (!isRepeatingEvent && isFrequencyNever) {
      return { updateEventService: GroupsService.UpdateGroupEvent };
    }
  }, [event, repeatingData]);

  const handleScroll = (event: {
    nativeEvent: {
      contentOffset: any;
      layoutMeasurement: any;
      contentSize: any;
    };
  }) => {
    const { contentOffset, layoutMeasurement, contentSize } = event.nativeEvent;

    const isAtTop = contentOffset.y <= 0;
    const isAtBottom =
      contentOffset.y + layoutMeasurement.height >= contentSize.height;

    // Show the top shadow when scrolling starts and hide it only at the very top
    setShowTopShadow(!isAtTop);

    // Show the bottom shadow always unless at the very end
    setShowBottomShadow(!isAtBottom);
  };

  const save = useCallback(() => {
    const sendEvent = (event: IGroupEvent | undefined) => {
      if (!event) {
        return;
      }

      if (isEventDetailsScreen) {
        navigation.navigate(Constants.CHAT_SCREEN);
      } else {
        navigation.goBack();
      }

      if (groupEventChatList && groupEventChatList.length > 0) {
        groupEventChatList.forEach((message) => {
          dispatch(
            updateEventMessage(channelData as GroupChannel, message.messageId, {
              groupEvent: event,
            })
          );
        });
      } else {
        dispatch(
          sendEventMessage(
            channelData as GroupChannel,
            {
              groupEvent: event,
            },
            t('groups:eventCreated')
          )
        );
      }
    };

    let repetitionRules: string | any[] = [];

    // Check if the event has repetition rules (rrule)
    if (route?.params?.eventData?.rrule || savedWebRepeatingData) {
      const startDateFormatted =
        startDate && startTime && formatDateForRules(startDate, startTime);
      const rrule =
        route.params?.eventData?.rrule || savedWebRepeatingData?.rrule;
      repetitionRules = rrule
        ? [`DTSTART;TZID=${timezone}:${startDateFormatted}`, rrule]
        : [];
    }

    const createGroupEvent = async () => {
      if (!group?.id || !group?._embedded?.calendar?.id) {
        KitSnack.show(t('groups:errorMessage'), KitSnackDuration.SHORT);

        return;
      }

      try {
        setIsSaving(true);

        // Make the API request
        const response = await createGroupEventService({
          body: {
            all_day: allDay,
            app_key: app.appKey,
            description: description,
            end_at:
              endDate &&
              endTime &&
              timezone &&
              getTimestampUTC(endDate, endTime, timezone),
            start_at:
              startDate &&
              startTime &&
              timezone &&
              getTimestampUTC(startDate, startTime, timezone),
            event_duration:
              startDate && startTime && endDate && endTime && timezone
                ? getEventDuration(
                    startDate,
                    startTime,
                    endDate,
                    endTime,
                    timezone
                  )
                : undefined,
            published: true,
            timezone: timezone,
            title: title,
            event_title: title,
            send_attendance_reminder: attendanceReminder,
            _embedded: {
              group: {
                id: group.id,
              },
              calendar: {
                id: group._embedded.calendar.id,
              },
              location: !isLocationRemoved
                ? { id: groupEventLocationData?.id }
                : null,
              // @ts-ignore
              images: imageTypesData.map((item) => ({
                id: item.id,
                type:
                  item.type === 'group events wide'
                    ? 'wide'
                    : item.type === 'group events banner'
                    ? 'banner'
                    : item.type === 'group events square'
                    ? 'square'
                    : item.type,
              })),
            },
            // Conditionally add repetition_rules if it exists
            ...(repetitionRules.length > 0 && {
              repetition_rules: repetitionRules as string[],
              num_events_display: 5,
              update_type: 'unedited',
              published_at: currentDatetime,
            }),
          },
          token: accessToken,
        });

        // Check if the request was successful
        if (response.status === 201 && response.body) {
          setIsSaving(false);
          onCancel?.(false);
          onSuccess?.(true);
          sendEvent(response.body);
          KitSnack.show(t('groups:groupEventCreated'), KitSnackDuration.SHORT);
          deleteOldPhotos();
        } else {
          setIsSaving(false);
          if (
            response.status &&
            !excludedStatusCodes.includes(response.status)
          ) {
            HealthMonitor.logError(response.message);
          }

          KitSnack.show(t('groups:errorMessage'), KitSnackDuration.SHORT);
        }
      } catch (error) {
        KitSnack.show(t('groups:errorMessage'), KitSnackDuration.SHORT);
        HealthMonitor.logError(error);
      }
    };

    const updateGroupEvent = async (eventId: string) => {
      try {
        setIsSaving(true);

        // Get the correct service(s) based on the event data
        const { createEventService, updateEventService, deleteEventService } =
          getUpdateGroupEventService() as {
            createEventService?: EventServiceFunction;
            updateEventService: EventServiceFunction;
            deleteEventService?: EventServiceFunction;
          };

        // Handle creation and deletion when switching between repeating and non-repeating events
        if (createEventService && deleteEventService) {
          // Pass the necessary params to deleteEventService
          await deleteEventService({
            token: accessToken,
            id: eventId,
          });

          // Create the new event (either repeating or non-repeating)
          const response = await createEventService({
            body: {
              all_day: allDay,
              app_key: app.appKey,
              description: description,
              end_at:
                endDate &&
                endTime &&
                timezone &&
                getTimestampUTC(endDate, endTime, timezone),
              start_at:
                startDate &&
                startTime &&
                timezone &&
                getTimestampUTC(startDate, startTime, timezone),
              event_duration:
                startDate && startTime && endDate && endTime && timezone
                  ? getEventDuration(
                      startDate,
                      startTime,
                      endDate,
                      endTime,
                      timezone
                    )
                  : undefined,
              published: true,
              timezone: timezone,
              title: title,
              event_title: title,
              visibility: 'public',
              send_attendance_reminder: attendanceReminder,
              _embedded: {
                group: {
                  id: group?.id,
                },
                calendar: {
                  id: group?._embedded?.calendar?.id,
                },
                location: {
                  id: groupEventLocationData?.id,
                },
                images: imageTypesData.map((item) => ({
                  id: item.id,
                  type:
                    item.type === 'group events wide'
                      ? 'wide'
                      : item.type === 'group events banner'
                      ? 'banner'
                      : item.type === 'group events square'
                      ? 'square'
                      : item.type,
                })) as any,
              },
              // Conditionally add repetition_rules if it exists
              ...(repetitionRules.length > 0 && {
                repetition_rules: repetitionRules as string[],
                num_events_display: 5,
                update_type: 'unedited',
                published_at: currentDatetime,
              }),
            },
            token: accessToken,
            id: eventId,
          });

          if (response.status === 201 && response.body) {
            setIsSaving(false);
            onCancel?.(false);
            onSuccess?.(true);
            sendEvent(response.body);
            setNewGroupEvent?.(response.body);
            KitSnack.show(
              t('groups:groupEventUpdated'),
              KitSnackDuration.SHORT
            );
            deleteOldPhotos();
          } else {
            setIsSaving(false);
            if (
              response.status &&
              !excludedStatusCodes.includes(response.status)
            ) {
              HealthMonitor.logError(response.message);
            }

            KitSnack.show(t('groups:errorMessage'), KitSnackDuration.SHORT);
          }
        } else {
          // If only update, just update the event
          const response = await updateEventService({
            body: {
              all_day: allDay,
              app_key: app.appKey,
              description: description,
              send_attendance_reminder: attendanceReminder,
              end_at:
                endDate &&
                endTime &&
                timezone &&
                getTimestampUTC(endDate, endTime, timezone),
              start_at:
                startDate &&
                startTime &&
                timezone &&
                getTimestampUTC(startDate, startTime, timezone),
              event_duration:
                startDate && startTime && endDate && endTime && timezone
                  ? getEventDuration(
                      startDate,
                      startTime,
                      endDate,
                      endTime,
                      timezone
                    )
                  : undefined,
              published: true,
              timezone: timezone,
              title: title,
              event_title: title,
              visibility: 'public',
              num_events_display: 5,
              update_type: 'unedited',
              published_at: currentDatetime,
              _embedded: {
                group: {
                  id: group?.id,
                },
                calendar: {
                  id: group?._embedded?.calendar?.id,
                },
                location: {
                  id: groupEventLocationData?.id,
                },
                images: imageTypesData.map((item) => ({
                  id: item.id,
                  type:
                    item.type === 'group events wide'
                      ? 'wide'
                      : item.type === 'group events banner'
                      ? 'banner'
                      : item.type === 'group events square'
                      ? 'square'
                      : item.type,
                })) as any,
              },
              // Conditionally add repetition_rules if it exists
              ...(repetitionRules.length > 0 && {
                repetition_rules: repetitionRules as string[],
                published_at: currentDatetime,
              }),
            },
            token: accessToken,
            id: eventId,
          });

          if (response.status === 200 && response.body) {
            setIsSaving(false);
            onCancel?.(false);
            onSuccess?.(true);
            sendEvent(response.body);
            KitSnack.show(
              t('groups:groupEventUpdated'),
              KitSnackDuration.SHORT
            );
            deleteOldPhotos();
          } else {
            setIsSaving(false);
            if (
              response.status &&
              !excludedStatusCodes.includes(response.status)
            ) {
              HealthMonitor.logError(response.message);
            }

            KitSnack.show(t('groups:errorMessage'), KitSnackDuration.SHORT);
          }
        }
      } catch (error) {
        HealthMonitor.logError(error);
      }
    };

    // Check if we are editing an existing event
    if (isEdited && event === undefined) {
      createGroupEvent();
    } else if (isEdited && event?.id) {
      updateGroupEvent(event.id);
    }
  }, [
    groupEventChatList,
    isEdited,
    event,
    dispatch,
    channelData,
    navigation,
    accessToken,
    allDay,
    app.appKey,
    description,
    deleteOldPhotos,
    endDate,
    endTime,
    timezone,
    startDate,
    startTime,
    title,
    group?.id,
    group?._embedded?.calendar?.id,
    imageTypesData,
    onCancel,
    onSuccess,
    t,
    groupEventLocationData,
    isLocationRemoved,
    createGroupEventService,
    route.params?.eventData?.rrule,
    savedWebRepeatingData,
    getUpdateGroupEventService,
    setNewGroupEvent,
    currentDatetime,
    attendanceReminder,
    isEventDetailsScreen,
  ]);

  const onChangeDescription = (text: string) => {
    setDescription(text);
  };

  const onChangeStartDate = useCallback(
    (_event?: any, selectedDate?: Date) => {
      if (!selectedDate) return;

      const startDate = moment(selectedDate).startOf('day').toDate();
      setStartDate(startDate);

      const updatedEndDate = adjustEndDate(
        moment(startDate).toDate(),
        eventDurationInDays
      );
      setEndDate(eventDurationInDays > 0 ? updatedEndDate : startDate);
    },
    [setStartDate, setEndDate, eventDurationInDays]
  );

  const onChangeEndDate = useCallback(
    (_event?: any, selectedDate?: Date) => {
      const endDate = moment(selectedDate).startOf('day').toDate();
      setEndDate(endDate);

      const start = moment(startDate);
      const end = moment(endDate);
      const duration = end.diff(start, 'days');
      setEventDurationInDays(duration);

      setPassTimeRangeValidation(
        passesTimeRangeValidation(
          moment(startTime, 'HHmm'),
          moment(endTime, 'HHmm'),
          moment(startDate),
          moment(endDate)
        )
      );

      setPassDateRangeValidation(
        passesDateRangeValidation(moment(startDate), moment(endDate))
      );
    },
    [
      startDate,
      startTime,
      endTime,
      setEndDate,
      setEventDurationInDays,
      setPassDateRangeValidation,
      setPassTimeRangeValidation,
    ]
  );

  const handleStartTimeChange = useCallback(
    (time: Date | null) => {
      if (time) {
        const formattedTime = moment(time).format('HHmm');
        const isSameDay = moment(startDate).isSame(moment(endDate), 'day');

        setStartTime(formattedTime);
        // Adjust start time and end time to maintain the event time range
        if (isSameDay) {
          const updatedEndTime = adjustEndTime(formattedTime, eventDuration);
          setEndTime(updatedEndTime);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventDuration]
  );

  const handleEndTimeChange = useCallback(
    (time: Date | null) => {
      if (time) {
        const formattedTime = moment(time).format('HHmm');
        setEndTime(formattedTime);

        const start = moment(startTime, 'HHmm');
        const end = moment(formattedTime, 'HHmm');

        // Calculate the difference in minutes
        const duration = end.diff(start, 'minutes');
        setEventDuration(duration);
        if (startDate && endDate) {
          setPassTimeRangeValidation(
            passesTimeRangeValidation(
              start,
              end,
              moment(startDate),
              moment(endDate)
            )
          );
        }
      }
    },
    [startTime, startDate, endDate]
  );

  // Checking if the location is edited
  const handleSetIsLocationEdited = (edited: boolean) => {
    setIsLocationEdited(edited);
  };
  // Checking if the repeating options are edited
  const handleSetIsRepeatsEdited = (edited: boolean) => {
    setIsRepeatsEdited(edited);
  };

  // Checking if the location is removed
  const onSetEventGroupLocation = (removed: boolean) => {
    setIsLocationRemoved(removed);
  };
  // Handling repeating events data for web
  const handleSaveAndCloseModal = (data: any) => {
    setSavedWebRepeatingData(data);
    setRepeatingEventScreenVisible(false);
    setIsEventImageEdited(false);
  };

  useEffect(() => {
    if (Platform.OS !== 'web' || isSmall) {
      navigation.setOptions({
        headerRight: () => (
          <KitTouchable
            disabled={!isEdited}
            onPress={save}
            style={{
              paddingLeft: 12,
              paddingRight: Platform.OS === 'ios' ? 12 : 18,
              paddingVertical: 6,
              marginRight: -6,
              borderRadius: 40,
            }}
          >
            <KitText
              color={isEdited ? Colors.brand : Colors.N400}
              fontSize={17}
              testID='group-events-create'
            >
              {route.params?.event
                ? t('common:save')
                : t('common:buttonTitleCreate')}
            </KitText>
          </KitTouchable>
        ),
        title: route.params?.event
          ? t('groups:editEvent')
          : t('groups:newGroupEvent'),
      });
    }
  }, [isEdited, navigation, save, t, isSmall, route.params?.event]);

  // Hooks

  // Get message list when attendance modal open directly from url
  useEffect(() => {
    const messageListQuery = channel?.createPreviousMessageListQuery();

    if (messageListQuery && isEventDetailsScreen) {
      dispatch(getPrevMessageList(messageListQuery));
    }
  }, [channel, dispatch, isEventDetailsScreen]);

  useEffect(() => {
    if (event?._embedded?.location) {
      setEventLocation(event?._embedded?.location as IGroupLocation);
    } else {
      setEventLocation(undefined);
    }
  }, [event, groupEventLocation, isLocationEdited]);

  if (repeatingEventScreenVisible && !isSmall) {
    return (
      <RepeatGroupEvent
        route={{
          params: {
            group: group,
            event: event,
            startDate: startDate,
            eventData: savedWebRepeatingData,
            handleSetIsRepeatsEdited,
          },
        }}
        navigation={navigation}
        onCancel={() => setRepeatingEventScreenVisible(false)}
        onSaveWeb={handleSaveAndCloseModal}
      />
    );
  }

  if (groupEventLocationScreenVisible && !isSmall) {
    return (
      <GroupEventLocation
        route={{
          params: {
            group: group,
            groupEvent: event,
            handleSetIsLocationEdited,
            onSetEventGroupLocation,
          },
        }}
        navigation={navigation}
        groupEventLocation={
          groupEventLocationData && !isLocationRemoved
            ? groupEventLocationData
            : undefined
        }
        setEventGroupLocation={setEventGroupLocation}
        onCancel={() => setGroupEventLocationScreenVisible(false)}
      />
    );
  }

  return (
    <>
      {Platform.OS === 'web' && sizeClass !== SizeClass.Small ? (
        <View
          style={[
            {
              padding: Spacing.xl,
              paddingBottom: Spacing.l,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            },
            showTopShadow && {
              backgroundColor: Colors.N0,
              shadowColor: 'rgba(0, 0, 0, 0.2)',
              shadowOffset: { width: 0, height: 3 },
              shadowOpacity: 1,
              shadowRadius: 10,
              zIndex: 999,
            },
          ]}
        >
          <KitText black bold fontSize={20}>
            {event ? t('groups:editEvent') : t('groups:newGroupEvent')}
          </KitText>
        </View>
      ) : null}
      <TouchableWithoutFeedback
        onPress={() => {
          startDateRef?.current?.closePicker?.();
          endDateRef?.current?.closePicker?.();
          startTimePickerRef?.current?.closePicker?.();
          endTimePickerRef?.current?.closePicker?.();
        }}
        accessible={false}
        style={{
          flex: 1,
        }}
      >
        <ScrollViewWrapper
          onScroll={handleScroll}
          keyboardShouldPersistTaps='handled'
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
        >
          <View
            style={{
              flex: 1,
              justifyContent: 'space-between',
            }}
          >
            <View
              style={{
                paddingTop:
                  Platform.OS === 'web' && isSmall
                    ? Spacing.l
                    : Platform.OS === 'web'
                    ? 0
                    : Spacing.m,
                paddingHorizontal:
                  Platform.OS === 'web' ? Spacing.xl : Spacing.l,
              }}
            >
              <KitInput
                autoCapitalize='sentences'
                placeholder={t('groups:untitledEvent')}
                inputRef={titleRef}
                marginBottom={18}
                onChangeText={setTitle}
                value={title}
                testID='group-events-title'
                autoFocus
              />

              <KitEditableImage
                label={t('groups:eventImage')}
                endUserId={user?.id || ''}
                appKey={app.appKey}
                imageId={eventImageId}
                imageUrl={eventPhotoUrlValue || undefined}
                onPhotoChange={(imageValue: {
                  id: string | undefined;
                  url: string | undefined;
                }) => {
                  setEventPhotoUrlValue(imageValue.url);
                  setIsEventImageEdited(true);
                }}
                token={accessToken}
                setImageTypesData={setImageTypesData}
                testID='group-events-create-upload-section'
                customImageTypes={[
                  'group events wide',
                  'group events banner',
                  'group events square',
                ]}
              />

              <KitText
                fontSize={14}
                fontWeight={600}
                color={Colors.N600}
                style={{ marginBottom: Spacing.s }}
              >
                {t('common:starts')}
              </KitText>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'center',
                }}
              >
                <View
                  style={[
                    allDay && {
                      flex: 1,
                      marginBottom: Platform.OS !== 'web' ? Spacing.l : 0,
                    },
                    {
                      width: '60%',
                    },
                  ]}
                >
                  <View
                    style={{
                      marginRight: allDay ? 0 : Spacing.m,
                      marginBottom:
                        allDay && Platform.OS === 'web' ? Spacing.l : 0,
                    }}
                  >
                    <KitDatePickerInput
                      ref={startDateRef}
                      date={startDate}
                      onChange={onChangeStartDate}
                      label=''
                      minimumDate={new Date()}
                      onShowPicker={() => {
                        if (!startDate) {
                          onChangeStartDate(undefined, new Date());
                        }
                      }}
                      testID='group-events-start-date'
                    />
                  </View>
                </View>

                {!allDay ? (
                  <View style={{ width: '40%' }}>
                    <KitTimePicker
                      ref={startTimePickerRef}
                      onTimeChange={(time) => handleStartTimeChange(time)}
                      initialTime={startTime}
                      style={{ marginBottom: Spacing.l }}
                    />
                  </View>
                ) : null}
              </View>

              <KitText
                fontSize={14}
                fontWeight={600}
                color={Colors.N600}
                style={{ marginBottom: Spacing.s }}
              >
                {t('common:ends')}
              </KitText>

              <View
                style={[
                  {
                    flexDirection: 'row',
                    justifyContent: 'center',
                  },
                  Platform.OS !== 'web' && { zIndex: 99999 },
                ]}
              >
                <View
                  style={[
                    allDay && {
                      flex: 1,
                    },
                    {
                      width: '60%',
                    },
                  ]}
                >
                  <View style={{ marginRight: allDay ? 0 : Spacing.m }}>
                    <KitDatePickerInput
                      ref={endDateRef}
                      date={endDate}
                      onChange={onChangeEndDate}
                      label=''
                      onShowPicker={() => {
                        setPassDateRangeValidation(true);
                        if (!startDate) {
                          onChangeEndDate(undefined, new Date());
                        }
                      }}
                      testID='group-events-end-date'
                      errorLabel={
                        !passDateRangeValidation
                          ? t('groups:endDateValidationMessage')
                          : null
                      }
                      isValid={passDateRangeValidation}
                      validationStyle={{ width: 327, left: 0 }}
                    />
                  </View>
                </View>

                {!allDay ? (
                  <View style={{ width: '40%' }}>
                    <KitTimePicker
                      ref={endTimePickerRef}
                      onTimeChange={(time) => handleEndTimeChange(time)}
                      initialTime={endTime}
                      errorLabel={
                        !passTimeRangeValidation
                          ? t('groups:endTimeValidationMessage')
                          : null
                      }
                      validationStyle={{ width: 325, right: 0 }}
                    />
                  </View>
                ) : null}
              </View>

              <KitListItem
                ImageComponent={
                  <KitIcon
                    name='sun'
                    color={Colors.N600}
                    size={20}
                    style={{ margin: Spacing.s }}
                  />
                }
                rightElement={
                  <KitToggle
                    style={{ marginRight: -Spacing.l }}
                    onToggle={() => setAllDay(!allDay)}
                    isOn={allDay}
                  />
                }
                rightIcon=''
                title={t('groups:allDay')}
                titleFontSize={16}
                titleLineHeight={22}
                isTitleBold={false}
                topBorder={false}
                iconColor={Colors.N300}
                minHeight={78}
              />
              <KitListItem
                ImageComponent={
                  <KitIcon
                    name='repeat'
                    color={Colors.N600}
                    size={20}
                    style={{ margin: Spacing.s }}
                  />
                }
                subtitle={repeatSubtitle ?? t('messaging:never')}
                rightIcon='arrow-right'
                title={t('groups:repeats')}
                titleFontSize={16}
                isTitleBold={false}
                subtitleFontSize={14}
                subtitleLineHeight={23}
                subtitleLength={0}
                topBorder={false}
                iconColor={Colors.N300}
                minHeight={78}
                showRightIconOnAndroid={true}
                onPress={
                  Platform.OS === 'web'
                    ? () => {
                        setTimeout(() => {
                          setRepeatingEventScreenVisible(true);
                        }, 500);
                      }
                    : () =>
                        navigation.navigate(Constants.GROUP_EVENT_REPEAT, {
                          startDate,
                          event: event,
                          eventData: route.params?.eventData,
                          group,
                          handleSetIsRepeatsEdited,
                        })
                }
              />
              <KitListItem
                ImageComponent={
                  <KitIcon
                    name='not-filled-location'
                    color={Colors.N600}
                    size={20}
                    style={{ margin: Spacing.s }}
                  />
                }
                rightIcon='arrow-right'
                title={
                  groupEventLocationData && !isLocationRemoved
                    ? t('groups:labelGroupLocation')
                    : t('groups:addLocation')
                }
                subtitle={
                  groupEventLocationData && !isLocationRemoved
                    ? groupEventLocationData.title
                      ? `${groupEventLocationData.title} • ${groupEventLocationData.subtitle}`
                      : groupEventLocationData.subtitle
                    : ''
                }
                titleFontSize={16}
                isTitleBold={false}
                subtitleFontSize={14}
                subtitleLineHeight={23}
                subtitleLength={0}
                topBorder={false}
                iconColor={Colors.N300}
                minHeight={78}
                showRightIconOnAndroid={true}
                onPress={
                  Platform.OS === 'web'
                    ? () => {
                        setTimeout(() => {
                          setGroupEventLocationScreenVisible(true);
                        }, 500);
                      }
                    : () =>
                        navigation.navigate(Constants.GROUP_EVENT_LOCATION, {
                          group,
                          groupEvent: event,
                          groupEventLocation:
                            groupEventLocationData && !isLocationRemoved
                              ? groupEventLocationData
                              : undefined,
                          handleSetIsLocationEdited,
                          onSetEventGroupLocation,
                        })
                }
              />
              {/* Wrap with group attendance feature flag group attendance*/}
              {isGroupAttendanceEnabled ? (
                <KitListItem
                  ImageComponent={
                    <KitIcon
                      name='email'
                      color={Colors.N600}
                      size={20}
                      style={{ margin: Spacing.s }}
                    />
                  }
                  rightElement={
                    <KitToggle
                      style={{ marginRight: -Spacing.l }}
                      onToggle={() =>
                        setAttendanceReminder(!attendanceReminder)
                      }
                      isOn={attendanceReminder}
                    />
                  }
                  rightIcon=''
                  title={t('groups:attendanceReminder')}
                  titleFontSize={16}
                  titleLineHeight={22}
                  isTitleBold={false}
                  topBorder={false}
                  iconColor={Colors.N300}
                  minHeight={78}
                />
              ) : null}

              <View style={{ marginBottom: Spacing.l, paddingTop: Spacing.xl }}>
                <KitText
                  semiBold
                  fontSize={14}
                  fontWeight={600}
                  style={{
                    color: Colors.N600,
                    marginBottom: Spacing.s,
                  }}
                  testID='group-events-description'
                >
                  {t('availability:unavailableModal.description')}
                </KitText>
                <KitInput
                  autoCapitalize='sentences'
                  style={{ maxHeight: 83 }}
                  placeholder={t('groups:newGroupEventDescription')}
                  onChangeText={onChangeDescription}
                  value={description}
                  multiline
                  testID='group-events-description-input'
                />
              </View>
            </View>
          </View>
        </ScrollViewWrapper>
      </TouchableWithoutFeedback>
      {Platform.OS === 'web' && sizeClass !== SizeClass.Small ? (
        <View
          style={[
            {
              padding: 24,
              borderBottomLeftRadius: 16,
              borderBottomRightRadius: 16,
              flexDirection: 'row-reverse',
            },
            showBottomShadow && {
              backgroundColor: Colors.N0,
              shadowColor: 'rgba(0, 0, 0, 0.2)',
              shadowOffset: { width: 0, height: -3 },
              shadowOpacity: 1,
              shadowRadius: 10,
              zIndex: 999,
            },
          ]}
        >
          <KitButton
            color={Colors.N900}
            title={event ? t('common:save') : t('common:buttonTitleCreate')}
            disabled={!isEdited}
            isLoading={isSaving}
            onPress={save}
            testID='group-events-create'
          />
          <KitButton
            secondary
            color={Colors.N100}
            title={t('common:buttonTitleCancel')}
            style={{
              marginRight: Spacing.m,
            }}
            onPress={() => onCancel?.(false)}
          />
        </View>
      ) : null}
    </>
  );
}

const ScrollViewWrapper = (props: ScrollViewProps) => {
  return (
    <>
      <KeyboardAwareScrollView
        {...props}
        style={{ flex: 1, backgroundColor: Colors.N0 }}
        extraScrollHeight={Platform.OS === 'web' ? 0 : 70}
      >
        {props.children}
        <PlatformKeyboardSpacer />
      </KeyboardAwareScrollView>
    </>
  );
};
